import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Crown, Star, Ticket, Music, Gamepad2, Search, Settings, RefreshCw, AlertCircle } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';
const API = API_URL;

export function AdminSubscriptionsTab() {
  const [settings, setSettings] = useState({
    is_live: false,
    show_pricing_page: false,
    allow_self_signup: false
  });
  const [clubs, setClubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [tiers, setTiers] = useState({});
  const [premiumFeatures, setPremiumFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [assignDialog, setAssignDialog] = useState({ open: false, club: null });
  const [assignForm, setAssignForm] = useState({ tier: 'premium', duration_days: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, clubsRes, statsRes, tiersRes] = await Promise.all([
        safeFetch(`${API}/api/subscriptions/admin/settings`),
        safeFetch(`${API}/api/subscriptions/admin/clubs`),
        safeFetch(`${API}/api/subscriptions/admin/stats`),
        safeFetch(`${API}/api/subscriptions/tiers`)
      ]);

      // safeFetch returns {ok, status, data} - data is already parsed
      if (settingsRes.ok) {
        setSettings(settingsRes.data);
      }
      if (clubsRes.ok) {
        setClubs(clubsRes.data.clubs || []);
      }
      if (statsRes.ok) {
        setStats(statsRes.data);
      }
      if (tiersRes.ok) {
        setTiers(tiersRes.data.tiers || {});
        setPremiumFeatures(tiersRes.data.premium_features || {});
      }
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSettingsChange = async (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    
    try {
      const res = await safeFetch(`${API}/api/subscriptions/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      
      if (res.ok) {
        toast.success('Settings updated');
      } else {
        toast.error('Failed to update settings');
        setSettings(settings); // Revert
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
      setSettings(settings); // Revert
    }
  };

  const handleAssignSubscription = async () => {
    if (!assignDialog.club) return;
    
    setSaving(true);
    try {
      const res = await safeFetch(`${API}/api/subscriptions/admin/clubs/${assignDialog.club.club_id}/subscription`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tier: assignForm.tier,
          duration_days: assignForm.duration_days ? parseInt(assignForm.duration_days) : null,
          notes: assignForm.notes || null
        })
      });
      
      // safeFetch returns {ok, status, data} - data is already parsed
      if (res.ok) {
        toast.success(res.data.message);
        setAssignDialog({ open: false, club: null });
        setAssignForm({ tier: 'premium', duration_days: '', notes: '' });
        fetchData();
      } else {
        toast.error(res.data.detail || 'Failed to assign subscription');
      }
    } catch (error) {
      console.error('Error assigning subscription:', error);
      toast.error('Failed to assign subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveSubscription = async (club) => {
    if (!window.confirm(`Downgrade "${club.name}" to Free tier? This will disable all premium features.`)) {
      return;
    }
    
    try {
      const res = await safeFetch(`${API}/api/subscriptions/admin/clubs/${club.club_id}/subscription`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        toast.success(`${club.name} downgraded to Free`);
        fetchData();
      } else {
        toast.error('Failed to remove subscription');
      }
    } catch (error) {
      console.error('Error removing subscription:', error);
      toast.error('Failed to remove subscription');
    }
  };

  // Filter clubs
  const filteredClubs = clubs.filter(club => {
    const matchesSearch = !searchQuery || 
      club.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const clubTier = club.subscription_tier || 'free';
    const matchesTier = filterTier === 'all' || clubTier === filterTier;
    return matchesSearch && matchesTier;
  });

  const getFeatureIcon = (feature) => {
    switch (feature) {
      case 'featured_club': return <Star className="h-4 w-4" />;
      case 'dj_live': return <Music className="h-4 w-4" />;
      case 'bingo_raffle': return <Gamepad2 className="h-4 w-4" />;
      case 'ticketing': return <Ticket className="h-4 w-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Stats */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500" />
            Subscription Management
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Manage club subscription tiers and premium features
          </p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-subscriptions-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* System Settings Card */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-amber-600" />
            System Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <div>
              <Label className="font-medium">Subscription System Live</Label>
              <p className="text-sm text-slate-500">When enabled, feature access is controlled by subscription tier</p>
            </div>
            <Switch
              checked={settings.is_live}
              onCheckedChange={(v) => handleSettingsChange('is_live', v)}
              data-testid="subscription-system-live-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <div>
              <Label className="font-medium">Show Pricing Page</Label>
              <p className="text-sm text-slate-500">Display pricing options to club owners</p>
            </div>
            <Switch
              checked={settings.show_pricing_page}
              onCheckedChange={(v) => handleSettingsChange('show_pricing_page', v)}
              data-testid="show-pricing-toggle"
            />
          </div>
          
          <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
            <div>
              <Label className="font-medium">Allow Self-Signup</Label>
              <p className="text-sm text-slate-500">Let clubs purchase premium subscriptions directly</p>
            </div>
            <Switch
              checked={settings.allow_self_signup}
              onCheckedChange={(v) => handleSettingsChange('allow_self_signup', v)}
              data-testid="allow-self-signup-toggle"
            />
          </div>

          {!settings.is_live && (
            <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">System Not Live</p>
                <p className="text-sm text-blue-600">
                  Feature access is currently controlled by individual toggles, not subscription tiers. 
                  You can still assign tiers to clubs for when the system goes live.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-slate-900">{stats.total_clubs}</div>
              <div className="text-sm text-slate-500">Total Clubs</div>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50/30">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-green-600">{stats.paid_clubs}</div>
              <div className="text-sm text-green-700">Premium Clubs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-slate-600">{stats.free_clubs}</div>
              <div className="text-sm text-slate-500">Free Clubs</div>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50/30">
            <CardContent className="pt-6">
              <div className="text-3xl font-bold text-amber-600">
                {stats.monthly_recurring_revenue > 0 ? `£${stats.monthly_recurring_revenue.toFixed(2)}` : '£0'}
              </div>
              <div className="text-sm text-amber-700">Potential MRR</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Premium Features Reference */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Premium Features</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(premiumFeatures).map(([key, feature]) => (
              <div key={key} className="p-3 bg-slate-50 rounded-lg border flex items-start gap-2">
                <div className="p-1.5 bg-amber-100 rounded text-amber-600">
                  {getFeatureIcon(key)}
                </div>
                <div>
                  <div className="font-medium text-sm">{feature.name}</div>
                  <div className="text-xs text-slate-500">{feature.description}</div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Clubs List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <CardTitle className="text-lg">Club Subscriptions</CardTitle>
            <div className="flex gap-2 w-full sm:w-auto">
              <div className="relative flex-1 sm:w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search clubs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-clubs-input"
                />
              </div>
              <Select value={filterTier} onValueChange={setFilterTier}>
                <SelectTrigger className="w-32" data-testid="filter-tier-select">
                  <SelectValue placeholder="All Tiers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {filteredClubs.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No clubs found matching your criteria
              </div>
            ) : (
              filteredClubs.map(club => {
                const tier = club.subscription_tier || 'free';
                const isPremium = tier === 'premium';
                
                return (
                  <div 
                    key={club.club_id} 
                    className={`p-4 rounded-lg border flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
                      isPremium ? 'bg-amber-50/50 border-amber-200' : 'bg-white'
                    }`}
                    data-testid={`club-row-${club.club_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${isPremium ? 'bg-amber-100' : 'bg-slate-100'}`}>
                        <Crown className={`h-5 w-5 ${isPremium ? 'text-amber-600' : 'text-slate-400'}`} />
                      </div>
                      <div>
                        <div className="font-medium text-slate-900">{club.name}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge 
                            variant={isPremium ? 'default' : 'secondary'} 
                            className={isPremium ? 'bg-amber-500' : ''}
                          >
                            {tier === 'premium' ? 'Premium' : 'Free'}
                          </Badge>
                          {club.subscription_expires_at && (
                            <span className="text-xs text-slate-500">
                              Expires: {new Date(club.subscription_expires_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {/* Active Features Indicators */}
                      <div className="flex gap-1 mr-2">
                        {club.is_featured && (
                          <div className="p-1 bg-amber-100 rounded" title="Featured Club">
                            <Star className="h-3.5 w-3.5 text-amber-600" />
                          </div>
                        )}
                        {club.dj_live_enabled && (
                          <div className="p-1 bg-purple-100 rounded" title="DJ Live">
                            <Music className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                        )}
                        {club.bingo_enabled && (
                          <div className="p-1 bg-green-100 rounded" title="Bingo/Raffle">
                            <Gamepad2 className="h-3.5 w-3.5 text-green-600" />
                          </div>
                        )}
                        {club.ticketing_enabled && (
                          <div className="p-1 bg-blue-100 rounded" title="Ticketing">
                            <Ticket className="h-3.5 w-3.5 text-blue-600" />
                          </div>
                        )}
                      </div>
                      
                      {isPremium ? (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleRemoveSubscription(club)}
                          className="text-red-600 hover:bg-red-50"
                          data-testid={`remove-subscription-${club.club_id}`}
                        >
                          Downgrade
                        </Button>
                      ) : (
                        <Button 
                          size="sm"
                          onClick={() => {
                            setAssignForm({ tier: 'premium', duration_days: '', notes: '' });
                            setAssignDialog({ open: true, club });
                          }}
                          className="bg-amber-500 hover:bg-amber-600"
                          data-testid={`assign-premium-${club.club_id}`}
                        >
                          <Crown className="h-4 w-4 mr-1" />
                          Upgrade
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assign Subscription Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => setAssignDialog({ open, club: assignDialog.club })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-amber-500" />
              Assign Subscription
            </DialogTitle>
          </DialogHeader>
          
          {assignDialog.club && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="font-medium">{assignDialog.club.name}</div>
                <div className="text-sm text-slate-500">Current: {assignDialog.club.subscription_tier || 'Free'}</div>
              </div>
              
              <div className="space-y-2">
                <Label>Subscription Tier</Label>
                <Select value={assignForm.tier} onValueChange={(v) => setAssignForm({...assignForm, tier: v})}>
                  <SelectTrigger data-testid="assign-tier-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  placeholder="Leave empty for permanent"
                  value={assignForm.duration_days}
                  onChange={(e) => setAssignForm({...assignForm, duration_days: e.target.value})}
                  data-testid="assign-duration-input"
                />
                <p className="text-xs text-slate-500">Leave empty for permanent subscription</p>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Reason for assignment..."
                  value={assignForm.notes}
                  onChange={(e) => setAssignForm({...assignForm, notes: e.target.value})}
                  data-testid="assign-notes-input"
                />
              </div>
              
              {assignForm.tier === 'premium' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="font-medium text-amber-800 mb-2">Features to be enabled:</div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex items-center gap-1.5 text-amber-700">
                      <Star className="h-4 w-4" /> Featured Club
                    </div>
                    <div className="flex items-center gap-1.5 text-amber-700">
                      <Music className="h-4 w-4" /> DJ Live
                    </div>
                    <div className="flex items-center gap-1.5 text-amber-700">
                      <Gamepad2 className="h-4 w-4" /> Bingo & Raffle
                    </div>
                    <div className="flex items-center gap-1.5 text-amber-700">
                      <Ticket className="h-4 w-4" /> Ticket Selling
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, club: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignSubscription} 
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-assign-subscription-btn"
            >
              {saving ? 'Saving...' : 'Assign Subscription'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
